"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.VMGlobalStorageUtils = void 0;
const inversify_1 = require("inversify");
const NodeGuards_1 = require("../node/NodeGuards");
const NodeFactory_1 = require("../node/NodeFactory");
const NodeUtils_1 = require("../node/NodeUtils");
const NodeAppender_1 = require("../node/NodeAppender");
let VMGlobalStorageUtils = class VMGlobalStorageUtils {
    wrapClassDeclarationWithGlobalStorage(classNode, programNode, vmGlobalsName) {
        if (!classNode.id) {
            return;
        }
        if (!classNode.id.name || typeof classNode.id.name !== 'string') {
            return;
        }
        const className = classNode.id.name;
        if (className === 'undefined') {
            return;
        }
        const globalStorageStatement = NodeFactory_1.NodeFactory.expressionStatementNode(NodeFactory_1.NodeFactory.assignmentExpressionNode('=', NodeFactory_1.NodeFactory.memberExpressionNode(NodeFactory_1.NodeFactory.identifierNode(vmGlobalsName), NodeFactory_1.NodeFactory.literalNode(className), true), NodeFactory_1.NodeFactory.identifierNode(className)));
        NodeUtils_1.NodeUtils.parentizeAst(globalStorageStatement);
        NodeAppender_1.NodeAppender.insertAfter(programNode, [globalStorageStatement], classNode);
    }
    wrapFunctionDeclarationWithGlobalStorage(functionNode, programNode, vmGlobalsName) {
        if (!functionNode.id) {
            return;
        }
        if (!functionNode.id.name || typeof functionNode.id.name !== 'string') {
            return;
        }
        const functionName = functionNode.id.name;
        if (functionName === 'undefined') {
            return;
        }
        const globalStorageStatement = NodeFactory_1.NodeFactory.expressionStatementNode(NodeFactory_1.NodeFactory.assignmentExpressionNode('=', NodeFactory_1.NodeFactory.memberExpressionNode(NodeFactory_1.NodeFactory.identifierNode(vmGlobalsName), NodeFactory_1.NodeFactory.literalNode(functionName), true), NodeFactory_1.NodeFactory.identifierNode(functionName)));
        NodeUtils_1.NodeUtils.parentizeAst(globalStorageStatement);
        NodeUtils_1.NodeUtils.parentizeNode(globalStorageStatement, programNode);
        const insertIndex = this.findInsertIndexForGlobalStorage(programNode);
        programNode.body.splice(insertIndex, 0, globalStorageStatement);
    }
    findInsertIndexForGlobalStorage(programNode) {
        return Math.min(3, programNode.body.length);
    }
    wrapVariableDeclarationWithGlobalStorage(varDeclNode, containerNode, vmGlobalsName) {
        for (const declarator of varDeclNode.declarations) {
            if (NodeGuards_1.NodeGuards.isIdentifierNode(declarator.id) && declarator.init) {
                const varName = declarator.id.name;
                if (!varName || varName === 'undefined') {
                    continue;
                }
                const globalStorageStatement = NodeFactory_1.NodeFactory.expressionStatementNode(NodeFactory_1.NodeFactory.assignmentExpressionNode('=', NodeFactory_1.NodeFactory.memberExpressionNode(NodeFactory_1.NodeFactory.identifierNode(vmGlobalsName), NodeFactory_1.NodeFactory.literalNode(varName), true), NodeFactory_1.NodeFactory.identifierNode(varName)));
                NodeUtils_1.NodeUtils.parentizeAst(globalStorageStatement);
                NodeAppender_1.NodeAppender.insertAfter(containerNode, [globalStorageStatement], varDeclNode);
            }
        }
    }
    transformTopLevelDeclarations(programNode, vmGlobalsName) {
        const vmRuntimeIndex = this.findVMRuntimeIndex(programNode);
        const declaredVars = new Set();
        const functionDeclarations = new Set();
        for (let i = vmRuntimeIndex; i < programNode.body.length; i++) {
            const statement = programNode.body[i];
            if (NodeGuards_1.NodeGuards.isVariableDeclarationNode(statement)) {
                for (const declarator of statement.declarations) {
                    if (NodeGuards_1.NodeGuards.isIdentifierNode(declarator.id)) {
                        declaredVars.add(declarator.id.name);
                    }
                }
            }
            else if (NodeGuards_1.NodeGuards.isFunctionDeclarationNode(statement) && statement.id) {
                functionDeclarations.add(statement.id.name);
            }
        }
        for (const funcName of functionDeclarations) {
            declaredVars.delete(funcName);
        }
        for (let i = vmRuntimeIndex; i < programNode.body.length; i++) {
            const statement = programNode.body[i];
            if (NodeGuards_1.NodeGuards.isVariableDeclarationNode(statement) &&
                (statement.kind === 'const' || statement.kind === 'let')) {
                for (const declarator of statement.declarations) {
                    if (NodeGuards_1.NodeGuards.isIdentifierNode(declarator.id) && declarator.init) {
                        const vmGlobalsAssignment = this.createVMGlobalsAssignment(vmGlobalsName, declarator.id.name, programNode);
                        programNode.body.splice(i + 1, 0, vmGlobalsAssignment);
                        i++;
                    }
                }
            }
            else if (NodeGuards_1.NodeGuards.isExpressionStatementNode(statement)) {
                const transformed = this.transformTopLevelAssignment(statement, declaredVars, vmGlobalsName, programNode);
                if (transformed) {
                    programNode.body[i] = transformed;
                }
            }
        }
        if (declaredVars.size > 0) {
            for (let i = vmRuntimeIndex; i < programNode.body.length; i++) {
                const statement = programNode.body[i];
                if (NodeGuards_1.NodeGuards.isFunctionDeclarationNode(statement)) {
                    continue;
                }
                if (NodeGuards_1.NodeGuards.isVariableDeclarationNode(statement)) {
                    for (const declarator of statement.declarations) {
                        if (declarator.init) {
                            this.replaceIdentifiersInExpression(declarator.init, declaredVars, vmGlobalsName);
                        }
                    }
                    continue;
                }
                this.replaceIdentifierReadsWithVMGlobals(statement, declaredVars, vmGlobalsName);
            }
        }
        return declaredVars;
    }
    findVMRuntimeIndex(programNode) {
        for (let i = 0; i < programNode.body.length; i++) {
            const statement = programNode.body[i];
            if (NodeGuards_1.NodeGuards.isVariableDeclarationNode(statement) && statement.kind === 'const') {
                return i + 1;
            }
        }
        return 0;
    }
    createVMGlobalsAssignment(vmGlobalsName, varName, programNode) {
        const assignment = NodeFactory_1.NodeFactory.expressionStatementNode(NodeFactory_1.NodeFactory.assignmentExpressionNode('=', NodeFactory_1.NodeFactory.memberExpressionNode(NodeFactory_1.NodeFactory.identifierNode(vmGlobalsName), NodeFactory_1.NodeFactory.identifierNode(varName)), NodeFactory_1.NodeFactory.identifierNode(varName)));
        NodeUtils_1.NodeUtils.parentizeAst(assignment);
        NodeUtils_1.NodeUtils.parentizeNode(assignment, programNode);
        return assignment;
    }
    replaceIdentifierReadsWithVMGlobals(node, declaredVars, vmGlobalsName) {
        if (!node) {
            return;
        }
        if (NodeGuards_1.NodeGuards.isExpressionStatementNode(node)) {
            this.replaceIdentifiersInExpression(node.expression, declaredVars, vmGlobalsName);
        }
        else if (NodeGuards_1.NodeGuards.isReturnStatementNode(node)) {
            if (node.argument) {
                this.replaceIdentifiersInExpression(node.argument, declaredVars, vmGlobalsName);
            }
        }
        else if (NodeGuards_1.NodeGuards.isIfStatementNode(node)) {
            this.replaceIdentifiersInExpression(node.test, declaredVars, vmGlobalsName);
            this.replaceIdentifierReadsWithVMGlobals(node.consequent, declaredVars, vmGlobalsName);
            if (node.alternate) {
                this.replaceIdentifierReadsWithVMGlobals(node.alternate, declaredVars, vmGlobalsName);
            }
        }
        else if (NodeGuards_1.NodeGuards.isBlockStatementNode(node)) {
            for (const statement of node.body) {
                this.replaceIdentifierReadsWithVMGlobals(statement, declaredVars, vmGlobalsName);
            }
        }
        else if (NodeGuards_1.NodeGuards.isTryStatementNode(node)) {
            const tryStmt = node;
            this.replaceIdentifierReadsWithVMGlobals(tryStmt.block, declaredVars, vmGlobalsName);
            if (tryStmt.handler) {
                this.replaceIdentifierReadsWithVMGlobals(tryStmt.handler.body, declaredVars, vmGlobalsName);
            }
            if (tryStmt.finalizer) {
                this.replaceIdentifierReadsWithVMGlobals(tryStmt.finalizer, declaredVars, vmGlobalsName);
            }
        }
    }
    replaceIdentifiersInExpression(expression, declaredVars, vmGlobalsName) {
        if (!expression) {
            return;
        }
        if (NodeGuards_1.NodeGuards.isAssignmentExpressionNode(expression)) {
            if (NodeGuards_1.NodeGuards.isIdentifierNode(expression.left) && declaredVars.has(expression.left.name)) {
                const varName = expression.left.name;
                expression.left.type = 'MemberExpression';
                expression.left.object = NodeFactory_1.NodeFactory.identifierNode(vmGlobalsName);
                expression.left.property = NodeFactory_1.NodeFactory.literalNode(varName);
                expression.left.computed = true;
                delete expression.left.name;
            }
            this.replaceIdentifiersInExpression(expression.right, declaredVars, vmGlobalsName);
            return;
        }
        if (NodeGuards_1.NodeGuards.isSequenceExpressionNode(expression)) {
            for (const expr of expression.expressions) {
                this.replaceIdentifiersInExpression(expr, declaredVars, vmGlobalsName);
            }
            return;
        }
        if (NodeGuards_1.NodeGuards.isBinaryExpressionNode(expression) || NodeGuards_1.NodeGuards.isLogicalExpressionNode(expression)) {
            const binaryOrLogical = expression;
            this.replaceIdentifiersInExpression(binaryOrLogical.left, declaredVars, vmGlobalsName);
            this.replaceIdentifiersInExpression(binaryOrLogical.right, declaredVars, vmGlobalsName);
            return;
        }
        if (NodeGuards_1.NodeGuards.isConditionalExpressionNode(expression)) {
            this.replaceIdentifiersInExpression(expression.test, declaredVars, vmGlobalsName);
            this.replaceIdentifiersInExpression(expression.consequent, declaredVars, vmGlobalsName);
            this.replaceIdentifiersInExpression(expression.alternate, declaredVars, vmGlobalsName);
            return;
        }
        if (NodeGuards_1.NodeGuards.isNewExpressionNode(expression)) {
            if (expression.callee.type !== 'Super') {
                this.replaceIdentifiersInExpression(expression.callee, declaredVars, vmGlobalsName);
            }
            for (const arg of expression.arguments) {
                if (NodeGuards_1.NodeGuards.isSpreadElementNode(arg)) {
                    this.replaceIdentifiersInExpression(arg.argument, declaredVars, vmGlobalsName);
                }
                else {
                    this.replaceIdentifiersInExpression(arg, declaredVars, vmGlobalsName);
                }
            }
            return;
        }
        if (NodeGuards_1.NodeGuards.isCallExpressionNode(expression)) {
            if (expression.callee.type !== 'Super') {
                this.replaceIdentifiersInExpression(expression.callee, declaredVars, vmGlobalsName);
            }
            for (const arg of expression.arguments) {
                if (NodeGuards_1.NodeGuards.isSpreadElementNode(arg)) {
                    this.replaceIdentifiersInExpression(arg.argument, declaredVars, vmGlobalsName);
                }
                else {
                    this.replaceIdentifiersInExpression(arg, declaredVars, vmGlobalsName);
                }
            }
            return;
        }
        if (NodeGuards_1.NodeGuards.isMemberExpressionNode(expression)) {
            if (expression.object.type !== 'Super') {
                this.replaceIdentifiersInExpression(expression.object, declaredVars, vmGlobalsName);
            }
            if (expression.computed && expression.property.type !== 'PrivateIdentifier') {
                this.replaceIdentifiersInExpression(expression.property, declaredVars, vmGlobalsName);
            }
            return;
        }
        if (NodeGuards_1.NodeGuards.isUnaryExpressionNode(expression)) {
            this.replaceIdentifiersInExpression(expression.argument, declaredVars, vmGlobalsName);
            return;
        }
        if (NodeGuards_1.NodeGuards.isUpdateExpressionNode(expression)) {
            return;
        }
        if (NodeGuards_1.NodeGuards.isArrayExpressionNode(expression)) {
            for (const element of expression.elements) {
                if (element && element.type === 'SpreadElement') {
                    this.replaceIdentifiersInExpression(element.argument, declaredVars, vmGlobalsName);
                }
                else if (element) {
                    this.replaceIdentifiersInExpression(element, declaredVars, vmGlobalsName);
                }
            }
            return;
        }
        if (NodeGuards_1.NodeGuards.isObjectExpressionNode(expression)) {
            for (const property of expression.properties) {
                if (property.type === 'Property') {
                    if (property.computed && property.key.type !== 'PrivateIdentifier') {
                        this.replaceIdentifiersInExpression(property.key, declaredVars, vmGlobalsName);
                    }
                    if (property.value.type !== 'ObjectPattern' &&
                        property.value.type !== 'ArrayPattern' &&
                        property.value.type !== 'RestElement' &&
                        property.value.type !== 'AssignmentPattern') {
                        this.replaceIdentifiersInExpression(property.value, declaredVars, vmGlobalsName);
                    }
                }
                else if (property.type === 'SpreadElement') {
                    this.replaceIdentifiersInExpression(property.argument, declaredVars, vmGlobalsName);
                }
            }
            return;
        }
        if (NodeGuards_1.NodeGuards.isTemplateLiteralNode(expression)) {
            for (const expr of expression.expressions) {
                this.replaceIdentifiersInExpression(expr, declaredVars, vmGlobalsName);
            }
            return;
        }
        if (NodeGuards_1.NodeGuards.isIdentifierNode(expression)) {
            const varName = expression.name;
            if (declaredVars.has(varName)) {
                expression.type = 'MemberExpression';
                expression.object = NodeFactory_1.NodeFactory.identifierNode(vmGlobalsName);
                expression.property = NodeFactory_1.NodeFactory.literalNode(varName);
                expression.computed = true;
                delete expression.name;
            }
        }
    }
    transformTopLevelAssignment(statement, declaredVars, vmGlobalsName, programNode) {
        const expression = statement.expression;
        if (NodeGuards_1.NodeGuards.isSequenceExpressionNode(expression)) {
            let transformed = false;
            const newExpressions = expression.expressions.map((expr) => {
                const wrapped = this.wrapAssignmentWithGlobalStorage(expr, declaredVars, vmGlobalsName);
                if (wrapped) {
                    transformed = true;
                    return wrapped;
                }
                return expr;
            });
            if (transformed) {
                const newStatement = NodeFactory_1.NodeFactory.expressionStatementNode(NodeFactory_1.NodeFactory.sequenceExpressionNode(newExpressions));
                NodeUtils_1.NodeUtils.parentizeAst(newStatement);
                NodeUtils_1.NodeUtils.parentizeNode(newStatement, programNode);
                return newStatement;
            }
            return null;
        }
        const wrapped = this.wrapAssignmentWithGlobalStorage(expression, declaredVars, vmGlobalsName);
        if (wrapped) {
            const wrappedStatement = NodeFactory_1.NodeFactory.expressionStatementNode(wrapped);
            NodeUtils_1.NodeUtils.parentizeAst(wrappedStatement);
            NodeUtils_1.NodeUtils.parentizeNode(wrappedStatement, programNode);
            return wrappedStatement;
        }
        return null;
    }
    wrapAssignmentWithGlobalStorage(expression, declaredVars, vmGlobalsName) {
        if (!NodeGuards_1.NodeGuards.isAssignmentExpressionNode(expression)) {
            return null;
        }
        if (!NodeGuards_1.NodeGuards.isIdentifierNode(expression.left)) {
            return null;
        }
        const varName = expression.left.name;
        if (!declaredVars.has(varName)) {
            return null;
        }
        return NodeFactory_1.NodeFactory.assignmentExpressionNode('=', NodeFactory_1.NodeFactory.memberExpressionNode(NodeFactory_1.NodeFactory.identifierNode(vmGlobalsName), NodeFactory_1.NodeFactory.literalNode(varName), true), expression);
    }
};
exports.VMGlobalStorageUtils = VMGlobalStorageUtils;
exports.VMGlobalStorageUtils = VMGlobalStorageUtils = __decorate([
    (0, inversify_1.injectable)()
], VMGlobalStorageUtils);
